import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { HomeTaskView } from '@/components/HomeTaskModal/HomeTaskView';
import { HomeworkCard } from '@/components/HomeworksModal/HomeworkCard';
import { HomeworkDateHeader } from '@/components/HomeworksModal/HomeworkDateHeader';
import { HomeworksPaywall } from '@/components/HomeworksModal/HomeworksPaywall';
import { useHomeworks } from '@/components/HomeworksModal/hooks/useHomeworks';
import { Modal } from '@/components/Modal';
import { ModalHeader } from '@/components/Modal/ModalHeader';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import type { Attachment } from '@/types/attachment';

interface Props {
  onClose: () => void;
}

export const HomeworksModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const showSubscriptionOffer = useShowSubscriptionOffer();
  const { groups, isLoading } = useHomeworks();
  const { progress, handleScroll } = useScrollFadeProgress();
  const isMobile = useIsMobile();
  const [openHomeTaskId, setOpenHomeTaskId] = useState<number | null>(null);
  const [hasNavigated, setHasNavigated] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );

  const openHomeTask = (homeTaskId: number) => {
    setHasNavigated(true);
    setOpenHomeTaskId(homeTaskId);
  };

  const closeHomeTask = () => {
    setHasNavigated(true);
    setOpenHomeTaskId(null);
  };

  const isListVisible = openHomeTaskId === null;
  const listAnimationClassName =
    hasNavigated && isListVisible ? 'max-md:animate-zoom-in' : '';

  const title = t('diary.homeworks.title');
  const isScrolled = progress > 0.5;
  const isSpinnerVisible = !showSubscriptionOffer && isLoading;
  const isEmptyStateVisible =
    !showSubscriptionOffer && !isLoading && groups.length === 0;
  const areGroupsVisible =
    !showSubscriptionOffer && !isLoading && groups.length > 0;

  useLayoutEffect(() => {
    const list = listRef.current;

    if (!list || !hasNavigated || !isMobile || !isListVisible) {
      return;
    }

    list.style.animation = 'none';
    list.getBoundingClientRect();
    list.style.animation = '';
  }, [hasNavigated, isMobile, isListVisible]);

  return (
    <Modal
      isOpen
      label={title}
      className="md:h-[788px] md:max-h-[85svh] md:w-[min(853px,calc(100vw-40px))]"
      onClose={onClose}
    >
      <div
        ref={listRef}
        className={`relative flex min-h-0 flex-1 flex-col ${listAnimationClassName}`}
      >
        <ModalHeader
          isMobileTitleHidden={!isScrolled}
          title={title}
          onClose={onClose}
        />

        <AutoHideScrollArea
          hasTopShadow
          className="flex min-h-0 flex-1 flex-col"
          viewportClassName="[&>div]:h-full"
          onScroll={handleScroll}
        >
          <div className="flex min-h-full flex-col gap-5 px-4 pb-[calc(16px+env(safe-area-inset-bottom))] md:px-5 md:pb-4">
            <div className="flex flex-col gap-3 md:hidden">
              <h1 className="text-[24px]/[30px] font-semibold text-foreground">
                {title}
              </h1>
              <p className="text-body-14 text-subtle-foreground">
                {t('diary.homeworks.subtitle')}
              </p>
            </div>

            {showSubscriptionOffer && <HomeworksPaywall />}

            {isSpinnerVisible && (
              <div className="flex flex-1 items-center justify-center">
                <PageSpinner className="size-[22px] text-subtle-foreground" />
              </div>
            )}

            {isEmptyStateVisible && (
              <EmptyState label={t('diary.homeworks.empty')} />
            )}

            {areGroupsVisible &&
              groups.map((group) => (
                <section key={group.date} className="flex flex-col gap-3">
                  <HomeworkDateHeader date={group.date} />

                  {group.items.map((item) => (
                    <HomeworkCard
                      key={item.id}
                      item={item}
                      onOpenHomeTask={openHomeTask}
                      onPreview={setPreviewAttachment}
                    />
                  ))}
                </section>
              ))}
          </div>
        </AutoHideScrollArea>

        {openHomeTaskId !== null && (
          <div className="absolute inset-0 z-10 flex flex-col bg-background max-md:animate-zoom-in">
            <HomeTaskView
              homeTaskId={openHomeTaskId}
              onBack={closeHomeTask}
              onClose={onClose}
            />
          </div>
        )}
      </div>

      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          onDownloaded={() => setPreviewAttachment(null)}
        />
      )}
    </Modal>
  );
};
